import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { FieldCropsDataEnvelope } from "../../../models/mappings";

import { useFieldCropsQuery } from "./fieldCrops.query";

export * from "./fieldCrops.invalidate";

export const fieldCropsAccessError = new Error(
  "Attempted to access field crops data but it is not available",
);

// #########################
// ## useFieldCropsStatus ##
// #########################

export type ResultOfUseFieldCropsStatus = QueryStatus;
export type UseFieldCropsStatus = () => ResultOfUseFieldCropsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the field crops data.
 */
export const useFieldCropsStatus: UseFieldCropsStatus = () => {
  return useFieldCropsQuery({ watch: ["status"] }).status;
};

// ########################
// ## useFieldCropsError ##
// ########################

export type ResultOfUseFieldCropsError = AxiosError | null;
export type UseFieldCropsError = () => ResultOfUseFieldCropsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the field crops data
 * or null if no error occurred.
 */
export const useFieldCropsError: UseFieldCropsError = () => {
  return useFieldCropsQuery({ watch: ["error"] }).error;
};

// ############################
// ## useMaybeFieldCropsData ##
// ############################

export type ResultOfUseMaybeFieldCropsData = FieldCropsDataEnvelope | undefined;
export type UseMaybeFieldCropsData = () => ResultOfUseMaybeFieldCropsData;

/**
 * The returned value of this hook will contain
 * the field crops data, which may be undefined
 */
export const useMaybeFieldCropsData: UseMaybeFieldCropsData = () => {
  return useFieldCropsQuery({ watch: ["data"] }).data;
};

// #######################
// ## useFieldCropsData ##
// #######################

export type ResultOfUseFieldCropsData = FieldCropsDataEnvelope;
export type UseFieldCropsData = () => ResultOfUseFieldCropsData;

/**
 * The returned value of this hook will contain the field crops data.
 *
 * This hook is guaranteed to contain field crops data or will throw.
 */
export const useFieldCropsData: UseFieldCropsData = () => {
  const data = useMaybeFieldCropsData();
  if (data === undefined) {
    throw fieldCropsAccessError;
  }
  return data;
};

// ########################
// ## useMaybeFieldCrops ##
// ########################

export type ParamsOfUseMaybeFieldCrops = { forceDisable?: boolean };
export type ResultOfUseMaybeFieldCrops = QueryEnvelope<FieldCropsDataEnvelope>;
export type UseMaybeFieldCrops = (
  params?: ParamsOfUseMaybeFieldCrops,
) => ResultOfUseMaybeFieldCrops;

/**
 * The `data` property returned from this hook will contain
 * the field crops data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the field crops data.
 */
export const useMaybeFieldCrops: UseMaybeFieldCrops = ({
  forceDisable,
} = {}) => {
  const { status, error, data } = useFieldCropsQuery({
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ###################
// ## useFieldCrops ##
// ###################

export type ResultOfUseFieldCrops =
  EnsuredQueryEnvelope<FieldCropsDataEnvelope>;
export type UseFieldCrops = () => ResultOfUseFieldCrops;

/**
 * The `data` property returned from this hook will contain
 * the field crops data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the field crops data.
 *
 * This hook is guaranteed to contain field crops data or will throw.
 */
export const useFieldCrops: UseFieldCrops = () => {
  const { status, error, data } = useFieldCropsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw fieldCropsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
